import { codeFormKeyDownHandler } from 'helpers';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  UseFormSetFocus,
  useWatch,
} from 'react-hook-form';
import styled from 'styled-components';

import WarningIconSm from 'components/Icons/WarningIconSm';
import theme from 'theme/theme';

import {
  CodeContainer,
  CodeForm,
  FormInput,
  FormLabel,
  InputContainer,
} from './codeInputStyledElements';
import { ErrorText } from './StyledFormInputs';

type Props = {
  error: string;
  label: string;
  fields: string[];
  control: Control<FieldValues>;
  setFocus: UseFormSetFocus<FieldValues>;
  setIsCodeFilled: (value: boolean) => void;
  clearError: () => void;
};

const CodeInput = ({
  error,
  label,
  fields,
  control,
  setFocus,
  setIsCodeFilled,
  clearError,
}: Props) => {
  const allFieldsValues = useWatch({ control });

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const { value: inputValue, selectionStart } = e.target;

      let value = inputValue;

      if (selectionStart === 2) {
        [, value] = value.toString().split('');
      }

      const isNumber = !Number.isNaN(Number(value)) && value !== ' ';

      if (value && isNumber && value !== ' ') {
        if (index !== fields.length - 1) {
          setFocus(fields[index + 1]);
        } else {
          e.target.blur();
        }
      }

      isNumber && clearError();

      return isNumber ? value : '';
    },
    [clearError, fields, setFocus]
  );

  const isCodeInputFilled = useMemo(
    () =>
      fields.map((field) => allFieldsValues[field]).join('').length ===
      fields.length,
    [allFieldsValues, fields]
  );

  useEffect(() => {
    setIsCodeFilled(isCodeInputFilled);
  }, [isCodeInputFilled, setIsCodeFilled]);

  return (
    <CodeContainer>
      <CodeForm $codeLength={fields.length}>
        <FormLabel $error={error} $active>
          {label}
        </FormLabel>
        <>
          {fields.map((fieldNumber, i) => (
            <InputContainer key={fieldNumber} $error={error}>
              <Controller
                defaultValue=""
                name={fieldNumber}
                control={control}
                render={({ field }) => (
                  <FormInput
                    type="tel"
                    autoComplete="off"
                    data-testid="pin-input"
                    onKeyDown={(e) =>
                      codeFormKeyDownHandler(
                        e,
                        i,
                        setFocus,
                        fields,
                        field.value
                      )
                    }
                    {...field}
                    $error={error}
                    onSelect={(e) => {
                      if (e.currentTarget.selectionStart === 0 && field.value) {
                        e.currentTarget.selectionStart = 1;
                      }
                    }}
                    onChange={(e) => field.onChange(handleOnChange(e, i))}
                  />
                )}
              />
            </InputContainer>
          ))}
        </>
        {/* Added email input to prevent browser password manager hint */}
        <FormInput type="email" />
      </CodeForm>
      <ErrorTextContainer>
        <ErrorText $error={!!error}>
          <WarningIconSm color={theme.colors.primary} />
          {error}
        </ErrorText>
      </ErrorTextContainer>
    </CodeContainer>
  );
};

const ErrorTextContainer = styled.div`
  margin-top: 10px;
`;

export default memo(CodeInput);
